import React, { useEffect, useRef, useState } from 'react';
import { Icon, Avatar } from '../../../components/ui';
import { usePhone } from '../PhoneContext';
import { resolveNumber, searchCustomers, DialCandidate } from '../api';
import { ResolvedParty } from '../types';
import { normalizePhoneNumber, looksLikePhoneNumber } from '../phoneNumber';
import { RecentCallsPanel } from './RecentCallsPanel';

/**
 * Dialpad — a 3×4 grid dialer. Opens from the call bar; used to place
 * a fresh outbound call, or to send DTMF tones mid-call.
 *
 * Keyboard-drivable: number keys type, Enter dials, Backspace deletes,
 * Escape closes.
 */

const KEYS: { d: string; l: string }[] = [
  { d: '1', l: '' },    { d: '2', l: 'ABC' }, { d: '3', l: 'DEF' },
  { d: '4', l: 'GHI' }, { d: '5', l: 'JKL' }, { d: '6', l: 'MNO' },
  { d: '7', l: 'PQRS' },{ d: '8', l: 'TUV' }, { d: '9', l: 'WXYZ' },
  { d: '*', l: '' },    { d: '0', l: '+' },   { d: '#', l: '' },
];

// Does the query look like a name (has Hebrew/Latin letters) vs a number?
const looksLikeName = (v: string) => /[א-תa-zA-Z]/.test(v);

export const Dialpad: React.FC = () => {
  const { dialerOpen, closeDialer, startCall, call, sendDtmf, dnd, toggleDnd } = usePhone();
  const [value, setValue] = useState('');
  const [match, setMatch] = useState<ResolvedParty | null>(null);
  const [resolving, setResolving] = useState(false);
  const [nameResults, setNameResults] = useState<DialCandidate[] | null>(null);
  const inCall = call && (call.state === 'active' || call.state === 'held');
  const nameMode = !inCall && looksLikeName(value);
  // Was the mouse pressed down on the backdrop itself? Guards the
  // close-on-outside-click against text selections that end out there.
  const backdropPressRef = useRef(false);

  useEffect(() => { if (dialerOpen) { setValue(''); setMatch(null); setNameResults(null); } }, [dialerOpen]);

  // As the user types: search by name (letters) or resolve by number.
  // Mid-call the pad sends DTMF instead, so no lookup.
  useEffect(() => {
    if (inCall) return;
    if (looksLikeName(value)) {
      // Name search
      setMatch(null);
      if (value.trim().length < 2) { setNameResults(null); return; }
      const t = setTimeout(() => {
        searchCustomers(value.trim())
          .then(setNameResults)
          .catch(() => setNameResults([]));
      }, 300);
      return () => clearTimeout(t);
    }
    // Number resolve
    setNameResults(null);
    const digits = value.replace(/\D/g, '');
    if (digits.length < 7) { setMatch(null); setResolving(false); return; }
    setResolving(true);
    const t = setTimeout(() => {
      resolveNumber(value)
        .then(r => setMatch(r.found ? r : null))
        .catch(() => setMatch(null))
        .finally(() => setResolving(false));
    }, 350);
    return () => clearTimeout(t);
  }, [value, inCall]);

  const press = (d: string) => {
    if (inCall) { sendDtmf(d); return; } // mid-call → DTMF, no display edit
    setValue(v => (v + d).slice(0, 20));
  };
  // Normalize at the point of dialling too — the last line of defence, so a
  // number that reached the field by any route (paste, typing, keypad) is
  // dialable no matter what separators it still carries.
  const dial = () => {
    if (nameMode) return;
    const raw = value.trim();
    if (!raw) return;
    startCall(looksLikePhoneNumber(raw) ? normalizePhoneNumber(raw) : raw);
  };
  const dialCandidate = (c: DialCandidate) => startCall(c.phone);

  useEffect(() => {
    if (!dialerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { closeDialer(); return; }
      // When the text <input> is focused it already handles typing;
      // don't also append via the keypad path (that doubled digits).
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // In name-mode the text <input> drives everything.
      if (nameMode) return;
      if (/^[0-9*#+]$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace' && !inCall) setValue(v => v.slice(0, -1));
      else if (e.key === 'Enter' && !inCall) dial();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialerOpen, inCall, value, nameMode]);

  if (!dialerOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-end justify-start p-4 sm:items-center sm:justify-center bg-slate-900/40 backdrop-blur-sm animate-fade-in"
      // Close only when the press STARTED on the backdrop too. Selecting a
      // pasted number and dragging past the panel's edge releases the mouse
      // out here, and a plain onClick would read that as "clicked outside"
      // and close the dialer mid-edit.
      onMouseDown={e => { backdropPressRef.current = e.target === e.currentTarget; }}
      onClick={e => {
        if (e.target === e.currentTarget && backdropPressRef.current) closeDialer();
        backdropPressRef.current = false;
      }}
      role="dialog"
      aria-modal="true"
      aria-label="חייגן"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="flex flex-col sm:flex-row items-start gap-3 animate-scale-in"
      >
      {/* Recent calls sit beside the pad — redialling someone you just
          spoke to is the most common reason to open the dialer. Hidden
          mid-call, where the pad sends DTMF and a redial makes no sense.
          Under dir="rtl" the FIRST child lands on the right, which is
          where the team asked for it; `flex-row-reverse` would flip the
          row back to LTR and put it on the wrong side. */}
      {!inCall && <RecentCallsPanel />}
      <div className="w-full sm:w-[300px] rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="font-display text-sm font-bold text-slate-500">
            {inCall ? 'שליחת צלילים' : 'חייגן'}
          </span>
          <div className="flex items-center gap-1">
            {/* Do-not-disturb: when on, inbound calls don't ring me. */}
            {!inCall && (
              <button
                onClick={toggleDnd}
                title={dnd ? 'נא לא להפריע — פעיל (לחץ לביטול)' : 'נא לא להפריע — כבוי'}
                aria-label="נא לא להפריע"
                aria-pressed={dnd}
                className={[
                  'inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold transition',
                  dnd ? 'bg-red-500 text-white' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
                ].join(' ')}
              >
                <Icon name={dnd ? 'do_not_disturb_on' : 'do_not_disturb_off'} size={16} />
                {dnd ? 'נא לא להפריע' : ''}
              </button>
            )}
            <button onClick={closeDialer} className="size-7 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-400" aria-label="סגור">
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>

        {!inCall && (
          <div className="relative mb-4">
            <input
              value={value}
              // Allow digits, dial chars, spaces, and Hebrew/Latin
              // letters so the user can type either a number or a name.
              onChange={e => setValue(e.target.value.replace(/[^\d+*#\sא-תa-zA-Z'()./-]/g, ''))}
              // Reps paste numbers straight out of other systems in every
              // format ("+972…", "055-9-90-90-90"). Normalize the pasted
              // text so it is dialable without any retyping.
              onPaste={e => {
                const pasted = e.clipboardData.getData('text');
                if (!looksLikePhoneNumber(pasted)) return; // a name — leave it
                e.preventDefault();
                setValue(normalizePhoneNumber(pasted).slice(0, 20));
              }}
              // Also tidy anything typed by hand with separators, once the
              // field loses focus — never mid-typing, which would fight the
              // user's cursor.
              onBlur={() => {
                if (looksLikePhoneNumber(value)) setValue(normalizePhoneNumber(value));
              }}
              placeholder="מספר או שם לקוח"
              dir={nameMode ? 'rtl' : 'ltr'}
              className={[
                'w-full text-center font-display font-bold bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl py-2.5 px-9 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40',
                nameMode ? 'text-lg' : 'text-2xl tabular-nums',
              ].join(' ')}
            />
            {value && (
              <button
                onClick={() => setValue(v => v.slice(0, -1))}
                className="absolute inset-y-0 left-2 my-auto size-7 flex items-center justify-center text-slate-400 hover:text-slate-600"
                aria-label="מחק"
              >
                <Icon name="backspace" size={18} />
              </button>
            )}
          </div>
        )}

        {/* Name-search results — pick a customer to dial */}
        {nameMode && (
          <div className="mb-4 -mt-1 max-h-52 overflow-y-auto rounded-xl border border-slate-100 dark:border-slate-800">
            {nameResults === null ? (
              <div className="py-4 text-center text-xs text-slate-400">מחפש…</div>
            ) : nameResults.length === 0 ? (
              <div className="py-4 text-center text-xs text-slate-400">לא נמצאו לקוחות עם מספר טלפון</div>
            ) : (
              nameResults.map(c => (
                <button
                  key={c.id}
                  onClick={() => dialCandidate(c)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition text-start border-b border-slate-50 dark:border-slate-800 last:border-0"
                >
                  <Avatar name={c.fullName} size="xs" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{c.fullName}</div>
                    <div className="text-[11px] text-slate-500 truncate" dir="ltr">
                      {c.phone}{c.company ? ` · ${c.company}` : ''}
                    </div>
                  </div>
                  <Icon name="call" size={16} className="text-emerald-500 shrink-0" />
                </button>
              ))
            )}
          </div>
        )}

        {/* Live customer match while typing */}
        {!inCall && match?.customer && (
          <div className="flex items-center gap-2 mb-4 -mt-1 px-1 animate-fade-in">
            <Avatar name={match.customer.fullName} size="xs" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                {match.customer.fullName}
              </div>
              {match.customer.company?.name && (
                <div className="text-[11px] text-slate-500 truncate">{match.customer.company.name}</div>
              )}
            </div>
            {match.deal && (
              <span className="ms-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                <Icon name="handshake" size={12} /> עסקה
              </span>
            )}
          </div>
        )}
        {!inCall && resolving && !match && value.replace(/\D/g, '').length >= 7 && (
          <div className="text-[11px] text-slate-400 mb-4 -mt-1 px-1">מחפש לקוח…</div>
        )}

        {/* Keypad — hidden in name-search mode (results list replaces it) */}
        {!nameMode && (
          // Force LTR so the keypad reads like a phone (1-2-3 left→right);
          // the app's RTL context would otherwise mirror each row to 3-2-1.
          <div dir="ltr" className="grid grid-cols-3 gap-2.5">
            {KEYS.map(k => (
              <button
                key={k.d}
                onClick={() => press(k.d)}
                className="aspect-[1.35] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 active:scale-95 transition flex flex-col items-center justify-center leading-none"
              >
                <span className="font-display text-xl font-bold text-slate-800 dark:text-slate-100">{k.d}</span>
                <span className="text-[9px] tracking-[0.18em] text-slate-400 h-2.5">{k.l}</span>
              </button>
            ))}
          </div>
        )}

        {!inCall && !nameMode && (
          <button
            onClick={dial}
            disabled={!value.trim()}
            className="mt-4 w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-semibold py-3 flex items-center justify-center gap-2 transition"
          >
            <Icon name="call" size={20} /> חייג
          </button>
        )}
      </div>
      </div>
    </div>
  );
};
